import { useState } from 'react';

import { useAuth } from '../../../../hooks/auth';
import { useToast } from '../../../../hooks/toast';
import apiBackoffice from '../../../../services/apiBackoffice';

// Lista fechada: a Lambda NL_deleteBackofficeItem recusa qualquer outro valor.
// `exercise`, `quiz` e `attachment` exigem o patch em backend-patches/.
export type DeleteEntity =
  | 'course'
  | 'banner'
  | 'section'
  | 'module'
  | 'lesson'
  | 'exercise'
  | 'quiz'
  | 'attachment';

interface DeleteArgs {
  entity: DeleteEntity;
  id: string;
  successTitle: string;
  /** Mensagem de confirmação. Se omitida, a confirmação é delegada ao caller. */
  confirmMessage?: string;
  /** Se fornecida, substitui o `window.confirm` (confirmação acessível). */
  confirm?: () => Promise<boolean>;
}

export const useAdminDelete = () => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [deletingId, setDeletingId] = useState('');

  const deleteItem = async ({ entity, id, successTitle, confirmMessage, confirm }: DeleteArgs) => {
    if (deletingId) return false;

    if (confirm) {
      const accepted = await confirm();
      if (!accepted) return false;
    } else if (confirmMessage && !window.confirm(confirmMessage)) {
      return false;
    }

    setDeletingId(id);
    try {
      const response = await apiBackoffice.post('/backoffice/delete', {
        entity,
        id,
        schoolid: user.schoolid,
        userid: user.userid,
      });

      if (response.data?.message !== 'OK') {
        throw new Error('O servidor não confirmou a operação.');
      }

      addToast({ type: 'success', title: successTitle });
      return true;
    } catch (err: any) {
      // Alerta em vez de toast: o motivo da recusa (ex: seção com cursos) precisa ser lido.
      window.alert(
        err?.response?.data?.error || err?.message || 'Não foi possível concluir a operação.',
      );
      return false;
    } finally {
      setDeletingId('');
    }
  };

  return { deleteItem, deletingId };
};
